/**
 * Platform detection utilities for Tauri desktop app, Capacitor mobile, and custom domains
 */

import { isCapacitor } from '../api/config';

// True only for actual local development, NOT Capacitor mobile apps.
export const isLocalDev = !isCapacitor && (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ||
  window.location.hostname.endsWith('.panluma.local')
);

// Known main domain patterns - domains where Google OAuth is registered
// Users on these domains use standard SSO flow (no cross-domain redirect)
const MAIN_DOMAIN_PATTERNS = [
  /^app\.panluma\.ai$/i,  // Main application domain (sysadmin tenant default)
  /\.cloudfront\.net$/i,  // CloudFront distribution domain (legacy)
  /^localhost$/i,         // Local development (also used by Capacitor mobile)
  /^127\.0\.0\.1$/i,      // Local development
];

// Check if running in Tauri desktop environment
// When loading remote content, we detect via localStorage flag set by Tauri app
export function isTauriDesktop(): boolean {
  if (typeof window === 'undefined') return false;

  // Check for __TAURI__ global (works when frontend is bundled with Tauri)
  if ('__TAURI__' in window) return true;

  // Check user agent for Tauri
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('tauri')) return true;

  // Check localStorage flag set by the Tauri app on startup
  return localStorage.getItem('panluma_desktop_app') === 'true';
}

// Open URL in system browser (for OAuth flows in desktop app)
// Uses window.open which opens in external browser from WebView
export function openInSystemBrowser(url: string): void {
  // window.open with _blank opens in system browser from WebView
  const newWindow = window.open(url, '_blank');
  if (!newWindow) {
    // Fallback if popup blocked
    window.location.href = url;
  }
}

// Check if current domain is a custom domain (not main CloudFront domain)
export function isCustomDomain(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return !MAIN_DOMAIN_PATTERNS.some(pattern => pattern.test(hostname));
}

// Main application domain - used for OAuth callbacks from all custom domains
// This domain is registered with Google OAuth and is the default domain for the sysadmin tenant
const MAIN_APP_DOMAIN = 'https://app.panluma.ai';

// Get the main domain URL (CloudFront) for OAuth callbacks
// This is dynamically determined based on environment
export function getMainDomainOrigin(): string {
  // Capacitor apps that load from the bundle have a pseudo-hostname of "localhost"
  // (iOS uses capacitor://localhost, Android uses https://localhost per
  // androidScheme: 'https' in capacitor.config.ts). Their real domain — and the
  // host Google OAuth is registered against — is the main app domain. Must check
  // isCapacitor (Capacitor.isNativePlatform()), not just the protocol, otherwise
  // Android falls through to the localhost branch below and the OAuth redirect_uri
  // becomes https://localhost/... which Google rejects with redirect_uri_mismatch.
  if (isCapacitor) {
    return (import.meta.env.VITE_MAIN_DOMAIN_URL as string | undefined) || MAIN_APP_DOMAIN;
  }
  // For local development, use localhost (where Google OAuth redirect is registered)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return window.location.origin;
    }
    // Local subdomain routing: SSO must go through localhost (Google rejects .local TLDs)
    if (hostname.endsWith('.panluma.local')) {
      return `http://localhost:${window.location.port}`;
    }
  }
  // For production, use the CloudFront domain from environment
  // This is injected at build time via Vite
  const mainDomainUrl = import.meta.env.VITE_MAIN_DOMAIN_URL;

  // Validate VITE_MAIN_DOMAIN_URL is a proper URL (not just "https://")
  if (mainDomainUrl && mainDomainUrl.length > 10) {
    return mainDomainUrl;
  }

  // If on a custom domain without proper main domain URL, use the main app domain
  if (isCustomDomain()) {
    return MAIN_APP_DOMAIN;
  }

  // Fallback to current origin (should only happen on main domain)
  return window.location.origin;
}

// Check if running on a native platform (Tauri desktop or Capacitor mobile)
export function isNativePlatform(): boolean {
  return isTauriDesktop() || isCapacitor;
}

// Get the native platform identifier for OAuth state encoding.
// The trampoline page (NativeCallbackPage) reads this from the state
// to determine which custom scheme to redirect to.
export function getNativePlatform(): 'tauri' | 'capacitor' | null {
  if (isTauriDesktop()) return 'tauri';
  if (isCapacitor) return 'capacitor';
  return null;
}

// Get the appropriate redirect URI for OAuth based on platform
export function getOAuthRedirectUri(): string {
  // Native apps (desktop + mobile): use a web trampoline page that redirects
  // back to the app via custom scheme. The trampoline reads the platform from
  // the OAuth state to determine which scheme (panluma:// or ai.panluma.app://).
  if (isNativePlatform()) {
    return `${getMainDomainOrigin()}/auth/sso/native-callback`;
  }

  // For custom domains, OAuth must callback to main domain
  if (isCustomDomain()) {
    return `${getMainDomainOrigin()}/auth/sso/callback`;
  }

  // For main domain, use standard callback path
  return `${window.location.origin}/auth/sso/callback`;
}

/**
 * Apple SSO web flow: Apple uses response_mode=form_post and POSTs to its
 * registered redirect_uri. We need a backend endpoint for that — it bounces
 * the request back to the SPA callback page as a GET.
 */
export function getAppleOAuthRedirectUri(): string {
  return `${getMainDomainOrigin()}/api/v1/auth/sso/apple/form-callback`;
}

// Get the current domain host (without protocol, but including port if non-standard)
export function getCurrentDomain(): string {
  if (typeof window === 'undefined') return '';
  return window.location.host;
}

// Detect the client's operating system from user agent
export type ClientPlatform = 'macos' | 'windows' | 'linux' | 'android' | 'ios' | 'unknown';

export function getClientPlatform(): ClientPlatform {
  if (typeof navigator === 'undefined') return 'unknown';

  const ua = navigator.userAgent;

  // Mobile platforms (check before desktop since some mobile UAs contain desktop strings)
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'ios';

  // Desktop platforms
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macos';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Linux/i.test(ua)) return 'linux';

  return 'unknown';
}
